// =========================
// AddressAutocomplete
// Search-as-you-type address input backed by Google Places Autocomplete
// via our /api/geocode-autocomplete proxy. Selecting a result triggers
// /api/geocode-place to resolve lat/lng and emits onSelect({...}).
//
// Props:
//   onSelect({ address, lat, lng, placeId, components })
//   initialValue?: string
//   placeholder?:  string
//   country?:      string   (e.g. "us" — restrict suggestions)
// =========================

import React, { useEffect, useRef, useState } from "react";
import { API_BASE_URL } from "../services/api";

const DEBOUNCE_MS = 300;

function uuid() {
  // Lightweight session token (good enough for billing/session purposes)
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export default function AddressAutocomplete({
  onSelect,
  initialValue = "",
  placeholder = "Search address, RV park, or place name…",
  country = "us",
}) {
  const [value, setValue] = useState(initialValue);
  const [predictions, setPredictions] = useState([]);
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [unavailable, setUnavailable] = useState(false); // service-side issue, not user error
  const [resolved, setResolved] = useState(null);

  const sessionTokenRef = useRef(uuid());
  const debounceRef = useRef(null);
  const abortRef = useRef(null);
  const wrapperRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    function onDocClick(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  function fetchPredictions(q) {
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    const url =
      `${API_BASE_URL}/geocode-autocomplete?q=${encodeURIComponent(q)}` +
      `&sessiontoken=${sessionTokenRef.current}` +
      (country ? `&country=${country}` : "");

    setLoading(true);
    setError("");
    setUnavailable(false);

    fetch(url, { signal: ctrl.signal })
      .then(async (r) => {
        const data = await r.json().catch(() => ({}));
        if (!r.ok || !data.success) {
          // Treat server-side configuration / outage as "unavailable" rather
          // than a user input error — don't red-border the input.
          const msg = data.error || `Lookup failed (HTTP ${r.status})`;
          const looksLikeServerIssue =
            r.status >= 500 ||
            /GOOGLE_MAPS_API_KEY|not set|denied|unauthorized/i.test(msg);
          if (looksLikeServerIssue) {
            setUnavailable(true);
            setError("");
          } else {
            setError(msg);
          }
          setPredictions([]);
          return;
        }
        setPredictions(data.predictions || []);
        setOpen(true);
        setActiveIdx(-1);
      })
      .catch((err) => {
        if (err.name === "AbortError") return;
        // Network failure → treat as unavailable, not a user error.
        setUnavailable(true);
        setError("");
        setPredictions([]);
      })
      .finally(() => setLoading(false));
  }

  function handleChange(e) {
    const v = e.target.value;
    setValue(v);
    setResolved(null);

    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (v.trim().length < 2) {
      setPredictions([]);
      setOpen(false);
      return;
    }

    debounceRef.current = setTimeout(() => fetchPredictions(v.trim()), DEBOUNCE_MS);
  }

  async function selectPrediction(p) {
    setOpen(false);
    setValue(p.description);
    setLoading(true);
    setError("");

    try {
      const res = await fetch(
        `${API_BASE_URL}/geocode-place?place_id=${encodeURIComponent(p.place_id)}` +
          `&sessiontoken=${sessionTokenRef.current}`
      );
      const data = await res.json();
      if (!data.success) throw new Error(data.error || "Place lookup failed");
      if (typeof data.lat !== "number" || typeof data.lng !== "number") {
        throw new Error("No coordinates returned for this place");
      }

      const payload = {
        address: data.formattedAddress || p.description,
        lat: data.lat,
        lng: data.lng,
        placeId: data.place_id,
        components: data.components || {},
      };
      setResolved(payload);

      // Start a new session token for the next search
      sessionTokenRef.current = uuid();

      onSelect && onSelect(payload);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  function handleKeyDown(e) {
    if (!open || predictions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(predictions.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter") {
      if (activeIdx >= 0 && activeIdx < predictions.length) {
        e.preventDefault();
        selectPrediction(predictions[activeIdx]);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  }

  return (
    <div ref={wrapperRef} style={{ position: "relative", width: "100%" }}>
      <label
        style={{
          display: "block",
          fontWeight: "700",
          marginBottom: "12px",
          fontSize: "18px",
        }}
      >
        Property Address
      </label>

      <input
        type="text"
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={() => predictions.length > 0 && setOpen(true)}
        placeholder={placeholder}
        // Aggressively opt out of browser autofill + password-manager handling.
        // Chrome's autofill heuristic on free-text "address" fields is the
        // most likely source of the inline "string did not match the expected
        // pattern" message users sometimes see.
        autoComplete="new-password"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck="false"
        inputMode="text"
        data-1p-ignore="true"
        data-lpignore="true"
        data-form-type="other"
        name="rvstr-address-search"
        style={{
          width: "100%",
          padding: "18px",
          borderRadius: "14px",
          border: `1px solid ${error ? "#ef4444" : "#d1d5db"}`,
          fontSize: "20px",
          boxSizing: "border-box",
        }}
      />

      {/* Loading / error / unavailable / resolved hint below the input */}
      <div style={{ minHeight: 22, marginTop: 6, fontSize: 13 }}>
        {loading && <span style={{ color: "#6b7280" }}>Searching…</span>}
        {!loading && error && <span style={{ color: "#b91c1c" }}>{error}</span>}
        {!loading && !error && unavailable && (
          <span style={{ color: "#92400e" }}>
            ⚠ Address lookup unavailable — enter Latitude and Longitude manually below.
            {" "}
            <a
              href="https://docs.anthropic.com/_internal_set_up_google_maps"
              onClick={(e) => e.preventDefault()}
              style={{ color: "#92400e", textDecoration: "underline" }}
              title="See SETUP_GOOGLE_MAPS.md in the repo root"
            >
              (setup help)
            </a>
          </span>
        )}
        {!loading && !error && !unavailable && resolved && (
          <span style={{ color: "#16a34a" }}>
            ✓ {resolved.address} · {resolved.lat.toFixed(5)}, {resolved.lng.toFixed(5)}
          </span>
        )}
      </div>

      {/* Dropdown */}
      {open && predictions.length > 0 && (
        <ul
          style={{
            position: "absolute",
            top: "calc(100% - 12px)",
            left: 0,
            right: 0,
            zIndex: 10,
            margin: 0,
            padding: 6,
            listStyle: "none",
            background: "#ffffff",
            border: "1px solid #e5e7eb",
            borderRadius: 12,
            boxShadow: "0 8px 24px rgba(0,0,0,0.10)",
            maxHeight: 320,
            overflowY: "auto",
          }}
        >
          {predictions.map((p, i) => (
            <li
              key={p.place_id}
              onMouseDown={(e) => e.preventDefault() /* keep focus */}
              onClick={() => selectPrediction(p)}
              onMouseEnter={() => setActiveIdx(i)}
              style={{
                padding: "10px 12px",
                borderRadius: 8,
                cursor: "pointer",
                background: i === activeIdx ? "#eff6ff" : "transparent",
              }}
            >
              <div style={{ fontWeight: 600, color: "#111827", fontSize: 15 }}>
                {p.mainText}
              </div>
              <div style={{ fontSize: 12, color: "#6b7280" }}>{p.secondaryText}</div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
